import React, { useState } from "react";
import { Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Seo from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please email us directly at hello@rootedinamador.com");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="contact-sent">
        <Seo title="Contact Us" description="Get in touch with Rooted in Amador." path="/contact" />
        <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Message received</div>
        <h1 className="font-display text-5xl text-darkRoast">Thanks for reaching out.</h1>
        <p className="mt-6 text-earth max-w-md mx-auto">
          We'll get back to you within one business day. In the meantime, feel free to browse the shop.
        </p>
        <a
          href="/shop"
          className="inline-flex items-center gap-2 mt-10 bg-darkRoast text-cream rounded-full px-7 py-3.5 text-sm tracking-wider-2 uppercase hover:bg-sage transition-colors"
          data-testid="contact-browse-seeds-link"
        >
          Browse seeds
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-20" data-testid="contact-page">
      <Seo title="Contact Us" description="Get in touch with Rooted in Amador. We'd love to hear from you." path="/contact" />

      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Get in touch</div>
          <h1 className="font-display text-5xl text-darkRoast mb-6">We'd love to hear from you.</h1>
          <p className="text-earth leading-relaxed mb-12">
            Questions about an order, a seed variety, or just want to say hello? We're a small local operation and we read every message.
          </p>

          <div className="space-y-5 text-sm text-earth">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-sage flex-shrink-0" strokeWidth={1.5} />
              <a href="mailto:hello@rootedinamador.com" className="hover:text-darkRoast">hello@rootedinamador.com</a>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-sage flex-shrink-0" strokeWidth={1.5} />
              <span>Plymouth, CA 95669 · Amador County</span>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-darkRoast/10">
            <div className="text-xs tracking-wider-2 uppercase text-earth mb-2">Response time</div>
            <p className="text-sm text-earth">We typically respond within one business day, often sooner.</p>
          </div>
        </div>

        <div className="space-y-5">
          {[
            { field: "name", placeholder: "Your name", type: "text" },
            { field: "email", placeholder: "Email address", type: "email" },
            { field: "subject", placeholder: "Subject (optional)", type: "text" },
          ].map(({ field, placeholder, type }) => (
            <input
              key={field}
              type={type}
              value={form[field]}
              onChange={update(field)}
              placeholder={placeholder}
              data-testid={`contact-${field}-input`}
              className="w-full bg-transparent border-b border-darkRoast/20 py-3 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50"
            />
          ))}

          <textarea
            value={form.message}
            onChange={update("message")}
            placeholder="Your message"
            rows={6}
            data-testid="contact-message-input"
            className="w-full bg-transparent border-b border-darkRoast/20 py-3 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50 resize-none"
          />

          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={update("website")}
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
          />

          <button
            onClick={handleSubmit}
            disabled={loading}
            data-testid="contact-submit-button"
            className="w-full bg-darkRoast text-cream rounded-full py-3.5 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-50 mt-2"
          >
            {loading ? "Sending…" : "Send message"}
          </button>
        </div>
      </div>
    </div>
  );
}
